/**
 * VPE Quantity Handler
 * Ensures quantity inputs respect VPE (sales unit) requirements
 */

class VPEQuantityHandler {
  constructor() {
    this.init();
  }

  init() {
    // Handle all quantity inputs with VPE
    this.setupQuantityInputs();
    
    // Listen for dynamic content changes (like quick-add modals)
    document.addEventListener('DOMContentLoaded', () => {
      this.setupQuantityInputs();
    });
  }

  setupQuantityInputs() {
    // Find all quantity-input elements that have VPE enabled (data-vpe="true")
    const quantityContainers = document.querySelectorAll('quantity-input[data-vpe]');
    
    quantityContainers.forEach(container => {
      // Check if VPE is enabled (handle both string "true" and boolean true)
      // Note: Liquid outputs may include quotes and spaces, so we need to clean the value
      const vpeValue = container.dataset.vpe;
      const cleanVpeValue = vpeValue ? vpeValue.replace(/['"]/g, '').trim() : '';
      const vpeEnabled = cleanVpeValue === 'true' || vpeValue === true;
      
      if (vpeEnabled) {
        this.setupVPEQuantityContainer(container);
      }
    });
  }

  setupVPEQuantityContainer(container) {
    const vpeValue = parseInt(container.dataset.vpeValue) || 1;
    const input = container.querySelector('.quantity-input');
    const minusBtn = container.querySelector('button[name="minus"]');
    const plusBtn = container.querySelector('button[name="plus"]');

    if (!input || vpeValue <= 1) {
      return;
    }
    
    // If buttons are missing (like in quick-add modal), we can still set the correct value
    if (!minusBtn || !plusBtn) {
      // Clean up the input value (remove newlines/spaces) and ensure it's correct
      const currentValue = parseInt(input.value.toString().trim()) || 0;
      
      if (currentValue !== vpeValue) {
        input.value = vpeValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }

    // Check if already processed to avoid duplicate event listeners
    if (container.hasAttribute('data-vpe-processed')) {
      return;
    }
    container.setAttribute('data-vpe-processed', 'true');

    // Override button click handlers
    this.setupVPEButtons(input, minusBtn, plusBtn, vpeValue);
    
    // Override input validation
    this.setupVPEInputValidation(input, vpeValue);
    
    // Set initial value to VPE minimum if needed
    const currentValue = parseInt(input.value) || 0;
    
    if (currentValue < vpeValue) {
      input.value = vpeValue;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (currentValue % vpeValue !== 0) {
      // Round to nearest VPE multiple
      const roundedValue = Math.round(currentValue / vpeValue) * vpeValue;
      const newValue = Math.max(roundedValue, vpeValue);
      input.value = newValue;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  setupVPEButtons(input, minusBtn, plusBtn, vpeValue) {
    // Remove existing event listeners by cloning elements
    const newMinusBtn = minusBtn.cloneNode(true);
    const newPlusBtn = plusBtn.cloneNode(true);
    
    minusBtn.parentNode.replaceChild(newMinusBtn, minusBtn);
    plusBtn.parentNode.replaceChild(newPlusBtn, plusBtn);

    // Add VPE-aware event listeners
    newMinusBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.decreaseQuantity(input, vpeValue);
    });

    newPlusBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.increaseQuantity(input, vpeValue);
    });
  }

  setupVPEInputValidation(input, vpeValue) {
    // Validate on input change
    input.addEventListener('input', () => {
      this.validateVPEInput(input, vpeValue);
    });

    // Validate on blur (when user leaves the field)
    input.addEventListener('blur', () => {
      this.roundToVPEMultiple(input, vpeValue);
    });

    // Also validate on Enter key press
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.roundToVPEMultiple(input, vpeValue);
        input.blur(); // Remove focus to trigger any other validations
      }
    });

    // Prevent manual typing of invalid values
    input.addEventListener('keydown', (e) => {
      // Allow navigation keys
      if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        return;
      }
      
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        return;
      }
      
      // Only allow numbers
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
      }
    });
  }

  decreaseQuantity(input, vpeValue) {
    const currentValue = parseInt(input.value) || vpeValue;
    const newValue = Math.max(currentValue - vpeValue, vpeValue);
    
    this.updateQuantity(input, newValue);
  }

  increaseQuantity(input, vpeValue) {
    const currentValue = parseInt(input.value) || 0;
    const maxValue = parseInt(input.max) || Infinity;
    const newValue = currentValue + vpeValue;
    
    if (newValue <= maxValue) {
      this.updateQuantity(input, newValue);
    }
  }

  validateVPEInput(input, vpeValue) {
    const value = parseInt(input.value);
    
    if (isNaN(value) || value < vpeValue) {
      // Don't auto-correct while typing, just mark as invalid
      input.setCustomValidity(`Minimum quantity is ${vpeValue} pieces`);
    } else if (value % vpeValue !== 0) {
      input.setCustomValidity(`Quantity must be in multiples of ${vpeValue} pieces`);
    } else {
      input.setCustomValidity('');
    }
  }

  roundToVPEMultiple(input, vpeValue) {
    const value = parseInt(input.value);
    
    if (isNaN(value) || value < vpeValue) {
      this.updateQuantity(input, vpeValue);
      return;
    }
    
    // Round to nearest VPE multiple
    const roundedValue = Math.round(value / vpeValue) * vpeValue;
    const finalValue = Math.max(roundedValue, vpeValue);
    
    if (finalValue !== value) {
      this.updateQuantity(input, finalValue);
    }
  }

  updateQuantity(input, newValue) {
    const maxValue = parseInt(input.max) || Infinity;
    const finalValue = Math.min(newValue, maxValue);
    
    input.value = finalValue;
    input.setCustomValidity('');
    
    // Trigger change event for other scripts that might be listening
    const changeEvent = new Event('change', { bubbles: true });
    input.dispatchEvent(changeEvent);
    
    // Trigger input event for real-time updates
    const inputEvent = new Event('input', { bubbles: true });
    input.dispatchEvent(inputEvent);
  }
}

// Make VPEQuantityHandler globally available
window.VPEQuantityHandler = VPEQuantityHandler;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VPEQuantityHandler();
  });
} else {
  new VPEQuantityHandler();
}

// Reinitialize for dynamically loaded content (like quick-add modals)
document.addEventListener('shopify:section:load', () => {
  new VPEQuantityHandler();
});

// Reinitialize when cart drawer content is updated
document.addEventListener('cart:updated', () => {
  setTimeout(() => new VPEQuantityHandler(), 100);
});

// Listen for cart drawer opening/content changes
document.addEventListener('cart:refresh', () => {
  setTimeout(() => new VPEQuantityHandler(), 100);
});

// Also handle modal content loading
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'childList') {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const vpeContainers = node.querySelectorAll ? node.querySelectorAll('quantity-input[data-vpe]') : [];
          if (vpeContainers.length > 0 || (node.matches && node.matches('quantity-input[data-vpe]'))) {
            setTimeout(() => new VPEQuantityHandler(), 100);
          }
        }
      });
    }
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});  